import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Download, Wallet, Paperclip, FileText, Repeat } from "lucide-react";
import { api, apiError, money, fxNote, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Field, PeriodPicker, SummaryCard } from "@/components/PeriodPicker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const TONE = {
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  partial: "bg-amber-50 text-amber-700 border-amber-200",
  unpaid: "bg-red-50 text-red-700 border-red-200",
};

const today = () => new Date().toISOString().slice(0, 10);

const blank = () => ({
  date: today(),
  category: "",
  payee: "",
  description: "",
  amount: 0,
  tax_amount: 0,
  paid_amount: 0,
  paid_by: "",
  payment_method: "Bank Transfer",
  payment_date: "",
  currency: "INR",
  notes: "",
  is_recurring: false,
});

export default function Expenses() {
  const { can } = useAuth();
  const editable = can("expenses", "edit");
  const [period, setPeriod] = useState("this_month");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [category, setCategory] = useState("all");
  const [paidBy, setPaidBy] = useState("all");
  const [status, setStatus] = useState("all");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [payers, setPayers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(blank());
  const [fxRate, setFxRate] = useState(0);
  const [uploadFor, setUploadFor] = useState(null);
  const fileInput = useRef(null);

  const params = { period, start, end, category, paid_by: paidBy, payment_status: status, currency: "INR" };

  const load = useCallback(async () => {
    const [list, sum, cats, pay] = await Promise.all([
      api.get("/expenses", { params }),
      api.get("/expenses/summary", { params }),
      api.get("/expenses/categories"),
      api.get("/expenses/payers"),
    ]);
    setRows(list.data);
    setSummary(sum.data);
    setCategories(cats.data);
    setPayers(pay.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, start, end, category, paidBy, status]);

  useEffect(() => {
    load().catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [load]);

  useEffect(() => {
    api.get("/settings/fx").then((r) => setFxRate(Number(r.data.usd_to_inr) || 0)).catch(() => {});
  }, []);

  const pickReceipt = (id) => {
    setUploadFor(id);
    fileInput.current.value = "";
    fileInput.current.click();
  };

  const uploadReceipt = async (file) => {
    if (!file || !uploadFor) return;
    const body = new FormData();
    body.append("file", file);
    try {
      await api.post(`/expenses/${uploadFor}/receipt`, body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Receipt attached");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setUploadFor(null);
    }
  };

  const openReceipt = async (id) => {
    try {
      const res = await api.get(`/expenses/${id}/receipt`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch {
      toast.error("Could not open the receipt");
    }
  };

  const total = (Number(form.amount) || 0) + (Number(form.tax_amount) || 0);
  const remaining = total - (Number(form.paid_amount) || 0);

  const save = async () => {
    if (!form.category) return toast.error("Pick a category");
    const payload = {
      ...form,
      amount: Number(form.amount) || 0,
      tax_amount: Number(form.tax_amount) || 0,
      paid_amount: Number(form.paid_amount) || 0,
    };
    try {
      if (editId) await api.put(`/expenses/${editId}`, payload);
      else await api.post("/expenses", payload);
      toast.success(editId ? "Expense updated" : "Expense recorded");
      setOpen(false);
      setEditId(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("Expense deleted");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const exportXlsx = () => {
    const token = localStorage.getItem("adsdesk_token");
    const q = new URLSearchParams(params);
    fetch(`${API}/export/expenses?${q}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.blob() : Promise.reject(r)))
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `expenses-${period}.xlsx`;
        a.click();
        toast.success("Excel downloaded");
      })
      .catch(() => toast.error("Could not export"));
  };

  return (
    <div data-testid="expenses-page">
      <input
        ref={fileInput}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,.webp"
        data-testid="receipt-file-input"
        className="hidden"
        onChange={(e) => uploadReceipt(e.target.files?.[0])}
      />
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
            Money Out {summary?.start ? `· ${summary.start} to ${summary.end}` : "· all time"}
          </div>
          <h1 className="mt-2 font-display text-4xl font-black tracking-tight sm:text-5xl">Expenses</h1>
          <p className="mt-3 max-w-2xl text-sm text-slate-600">
            Salary, office rent, office expenses, software purchases, publisher payments and anything
            else going out — with who paid it and what is still pending.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            data-testid="expenses-export-button"
            className="rounded-md border-slate-300 active:scale-95"
            onClick={exportXlsx}
          >
            <Download className="mr-2 h-4 w-4" /> Export Excel
          </Button>
          {editable && (
            <Button
              data-testid="add-expense-button"
              className="rounded-md bg-slate-900 hover:bg-slate-800 active:scale-95"
              onClick={() => {
                setEditId(null);
                setForm({ ...blank(), category: categories[0]?.name || "" });
                setOpen(true);
              }}
            >
              <Plus className="mr-2 h-4 w-4" /> New Expense
            </Button>
          )}
        </div>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <SummaryCard
          label="Total Expenses"
          value={money(summary?.total_expenses)}
          sub={`${summary?.count || 0} records · ${fxNote(summary?.total_expenses, "INR", fxRate)}`}
          testid="expenses-total"
        />
        <SummaryCard
          label="Total Paid"
          value={money(summary?.total_paid)}
          sub={fxNote(summary?.total_paid, "INR", fxRate)}
          tone="text-emerald-600"
          testid="expenses-paid"
        />
        <SummaryCard
          label="Remaining"
          value={money(summary?.remaining)}
          sub={fxNote(summary?.remaining, "INR", fxRate)}
          tone={(summary?.remaining || 0) > 0 ? "text-red-600" : "text-slate-900"}
          testid="expenses-remaining"
        />
      </div>

      <div
        className="mb-8 grid grid-cols-2 gap-3 border border-slate-200 bg-slate-50 p-4 md:grid-cols-4 lg:grid-cols-6"
        data-testid="expenses-filters"
      >
        <PeriodPicker
          value={period}
          onChange={setPeriod}
          start={start}
          end={end}
          setStart={setStart}
          setEnd={setEnd}
          testid="expenses-period"
        />
        <Field label="Category">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="expenses-category-filter" className="h-9 border-slate-300 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border border-slate-200 bg-white">
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.name}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Payment paid by">
          <Select value={paidBy} onValueChange={setPaidBy}>
            <SelectTrigger data-testid="expenses-paidby-filter" className="h-9 border-slate-300 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border border-slate-200 bg-white">
              <SelectItem value="all">Anyone</SelectItem>
              {payers.map((p) => (
                <SelectItem key={p} value={p}>
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Status">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="expenses-status-filter" className="h-9 border-slate-300 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border border-slate-200 bg-white">
              {["all", "unpaid", "partial", "paid"].map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <div className="overflow-x-auto border border-slate-200 bg-white">
        <table className="w-full min-w-[1300px] text-sm" data-testid="expenses-table">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">
              <th className="px-3 py-3">Date</th>
              <th className="px-3 py-3">Category</th>
              <th className="px-3 py-3">Paid To</th>
              <th className="px-3 py-3">Description</th>
              <th className="px-3 py-3 text-right">Amount</th>
              <th className="px-3 py-3 text-right">Tax</th>
              <th className="px-3 py-3 text-right">Total</th>
              <th className="px-3 py-3 text-right">Paid</th>
              <th className="px-3 py-3 text-right">Remaining</th>
              <th className="px-3 py-3">Status</th>
              <th className="px-3 py-3">Payment Paid By</th>
              <th className="px-3 py-3">Receipt</th>
              <th className="px-3 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.id} className="transition-colors hover:bg-slate-50" data-testid={`expense-row-${r.id}`}>
                <td className="px-3 py-2 font-medium">{r.date}</td>
                <td className="px-3 py-2 font-semibold">
                  {r.category}
                  {r.is_recurring && (
                    <Repeat
                      className="ml-1 inline h-3 w-3 text-slate-400"
                      data-testid={`recurring-flag-${r.id}`}
                      title="Recurring template — copied into every new month"
                    />
                  )}
                </td>
                <td className="px-3 py-2">{r.payee || "—"}</td>
                <td className="px-3 py-2 text-slate-500">{r.description || "—"}</td>
                <td className="px-3 py-2 text-right">{money(r.amount, r.currency)}</td>
                <td className="px-3 py-2 text-right">{money(r.tax_amount, r.currency)}</td>
                <td className="px-3 py-2 text-right font-semibold">{money(r.total, r.currency)}</td>
                <td className="px-3 py-2 text-right">{money(r.paid_amount, r.currency)}</td>
                <td
                  className={`px-3 py-2 text-right font-bold ${
                    r.remaining > 0 ? "text-red-600" : "text-emerald-600"
                  }`}
                >
                  {money(r.remaining, r.currency)}
                </td>
                <td className="px-3 py-2">
                  <Badge variant="outline" className={`rounded-md border ${TONE[r.payment_status]}`}>
                    {r.payment_status}
                  </Badge>
                </td>
                <td className="px-3 py-2">
                  {r.paid_by || "—"}
                  {r.payment_method && (
                    <span className="ml-1 text-xs text-slate-500">({r.payment_method})</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {r.receipt_path ? (
                    <button
                      type="button"
                      data-testid={`view-receipt-${r.id}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-slate-900 underline decoration-slate-300 hover:decoration-slate-900"
                      onClick={() => openReceipt(r.id)}
                    >
                      <FileText className="h-3.5 w-3.5" /> View
                    </button>
                  ) : editable ? (
                    <button
                      type="button"
                      data-testid={`attach-receipt-${r.id}`}
                      className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900"
                      onClick={() => pickReceipt(r.id)}
                    >
                      <Paperclip className="h-3.5 w-3.5" /> Attach
                    </button>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <div className="flex justify-end gap-1">
                    {editable && r.receipt_path && (
                      <Button
                        size="icon"
                        variant="ghost"
                        data-testid={`replace-receipt-${r.id}`}
                        title="Replace receipt"
                        className="h-8 w-8"
                        onClick={() => pickReceipt(r.id)}
                      >
                        <Paperclip className="h-4 w-4" />
                      </Button>
                    )}                    {editable && (
                      <Button
                        size="icon"
                        variant="ghost"
                        data-testid={`edit-expense-${r.id}`}
                        className="h-8 w-8"
                        onClick={() => {
                          setEditId(r.id);
                          setForm({ ...blank(), ...r });
                          setOpen(true);
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                    {can("expenses", "delete") && (
                      <Button
                        size="icon"
                        variant="ghost"
                        data-testid={`delete-expense-${r.id}`}
                        className="h-8 w-8 text-red-600 hover:bg-red-50"
                        onClick={() => remove(r.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={13} className="px-3 py-10 text-center text-slate-500">
                  No expenses recorded for this period.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {summary?.by_category?.length > 0 && (
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="border border-slate-200 bg-white">
            <div className="border-b border-slate-200 px-5 py-3 font-display text-base font-bold">
              Spend by category
            </div>
            <div className="divide-y divide-slate-100" data-testid="expenses-by-category">
              {summary.by_category.map((c) => (
                <div key={c.name} className="flex items-center justify-between px-5 py-3 text-sm">
                  <span className="text-slate-600">{c.name}</span>
                  <span className="font-bold">{money(c.amount)}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="border border-slate-200 bg-white">
            <div className="border-b border-slate-200 px-5 py-3 font-display text-base font-bold">
              Paid by
            </div>
            <div className="divide-y divide-slate-100" data-testid="expenses-by-payer">
              {summary.by_payer.map((c) => (
                <div key={c.name} className="flex items-center justify-between px-5 py-3 text-sm">
                  <span className="flex items-center gap-2 text-slate-600">
                    <Wallet className="h-4 w-4 text-slate-400" /> {c.name}
                  </span>
                  <span className="font-bold">{money(c.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border border-slate-200 bg-white">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black tracking-tight">
              {editId ? "Edit expense" : "New expense"}
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Record what was spent, how much has actually been paid and who paid it.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-xs font-bold uppercase">Date</Label>
              <Input
                type="date"
                data-testid="expense-date-input"
                className="mt-1 h-10 border-slate-300"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="expense-category-select" className="mt-1 h-10 border-slate-300 bg-white">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="border border-slate-200 bg-white">
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.name}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase">Paid to</Label>
              <Input
                data-testid="expense-payee-input"
                placeholder="Vendor, publisher or employee"
                className="mt-1 h-10 border-slate-300"
                value={form.payee}
                onChange={(e) => setForm({ ...form, payee: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase">Description</Label>
              <Input
                data-testid="expense-description-input"
                className="mt-1 h-10 border-slate-300"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase">Amount</Label>
              <Input
                type="number"
                data-testid="expense-amount-input"
                className="mt-1 h-10 border-slate-300"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase">Tax / GST</Label>
              <Input
                type="number"
                data-testid="expense-tax-input"
                className="mt-1 h-10 border-slate-300"
                value={form.tax_amount}
                onChange={(e) => setForm({ ...form, tax_amount: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase">Paid amount</Label>
              <Input
                type="number"
                data-testid="expense-paid-input"
                className="mt-1 h-10 border-slate-300"
                value={form.paid_amount}
                onChange={(e) => setForm({ ...form, paid_amount: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase">Payment paid by</Label>
              <Input
                data-testid="expense-paidby-input"
                placeholder="Person or account"
                className="mt-1 h-10 border-slate-300"
                value={form.paid_by}
                onChange={(e) => setForm({ ...form, paid_by: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase">Method</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger data-testid="expense-method-select" className="mt-1 h-10 border-slate-300 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border border-slate-200 bg-white">
                  {["Bank Transfer", "UPI", "Cash", "Card", "Cheque", "Other"].map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase">Payment date</Label>
              <Input
                type="date"
                data-testid="expense-payment-date-input"
                className="mt-1 h-10 border-slate-300"
                value={form.payment_date || ""}
                onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase">Currency</Label>
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger data-testid="expense-currency-select" className="mt-1 h-10 border-slate-300 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border border-slate-200 bg-white">
                  <SelectItem value="INR">INR ₹</SelectItem>
                  <SelectItem value="USD">USD $</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase">Note</Label>
            <Textarea
              data-testid="expense-notes-input"
              className="mt-1 border-slate-300"
              value={form.notes || ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4 border-t border-slate-200 pt-4 text-sm">
            <label className="flex items-center gap-2 text-slate-600" htmlFor="expense-recurring-toggle">
              <input
                id="expense-recurring-toggle"
                type="checkbox"
                data-testid="expense-recurring-toggle"
                className="h-4 w-4 accent-slate-900"
                checked={!!form.is_recurring}
                onChange={(e) => setForm({ ...form, is_recurring: e.target.checked })}
              />
              Repeat this expense every month
            </label>
            <div className="flex gap-8">
              <span className="text-slate-500">
                Total {money(total, form.currency)}
                <span className="ml-2 text-slate-400" data-testid="expense-total-fx">
                  {fxNote(total, form.currency, fxRate)}
                </span>
              </span>
              <span className="font-bold" data-testid="expense-remaining-preview">
                Remaining {money(remaining, form.currency)}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="save-expense-button"
              className="rounded-md bg-slate-900 hover:bg-slate-800 active:scale-95"
              onClick={save}
            >
              Save expense
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
